import React from 'react';
import { motion } from 'framer-motion';
import { IconWrapper } from '../../components/common/IconWrapper';
import {
  HiStar,
  HiUsers,
  HiBuildingOffice,
  HiMiniSparkles,
  HiCheckCircle,
  HiGlobeAlt,
  HiCog,
  HiMapPin
} from 'react-icons/hi2';

const heroImage = 'https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=1200&q=60';

const keyMetrics = [
  { metric: 'New features added', value: '15+' },
  { metric: 'System modules enhanced', value: '8' },
  { metric: 'Donor records managed', value: '10K+' }
];

const results = [
  { metric: 'Donor Engagement', value: '+65%' },
  { metric: 'System Efficiency', value: '+80%' },
  { metric: 'Feature Adoption', value: '+90%' },
  { metric: 'Data Accuracy', value: '+95%' }
];

const painPoints = [
  'Existing system architecture needed modifications to support new donor management features',
  'Legacy system integration challenges when adding extended functionality',
  'Risk of disrupting current operations while implementing enhancements',
  'Need to maintain data integrity and system stability during feature additions',
  'Complexity of extending donor management capabilities without breaking existing workflows'
];

const strategy = [
  'Conducted comprehensive system analysis to identify integration points and modification requirements',
  'Designed modular architecture enhancements to seamlessly integrate new donor management features',
  'Developed extended donor management capabilities including tracking, engagement, and reporting modules',
  'Implemented careful system modifications ensuring backward compatibility with existing functionality',
  'Established robust testing protocols to validate enhancements without disrupting current operations'
];

const outcomes = [
  { title: 'Enhanced Donor Management', detail: 'Extended donor management features provide comprehensive tracking, engagement tools, and reporting capabilities, improving donor relationships by 65%' },
  { title: 'Seamless System Integration', detail: 'Successfully modified existing system architecture to support new features while maintaining 100% operational continuity and data integrity' },
  { title: 'Scalable Architecture', detail: 'Modular enhancements enable future feature additions without major system overhauls, supporting long-term organizational growth' }
];

const timeline = [
  { phase: 'System Analysis', duration: '2 weeks', focus: 'Analyze existing system architecture and identify modification requirements' },
  { phase: 'Design & Planning', duration: '3 weeks', focus: 'Design enhancement architecture and donor management feature specifications' },
  { phase: 'Development', duration: '8 weeks', focus: 'Develop new features and implement system modifications' },
  { phase: 'Integration & Testing', duration: '3 weeks', focus: 'Integrate enhancements with existing system and comprehensive testing' },
  { phase: 'Deployment & Training', duration: '2 weeks', focus: 'Production deployment and user training for new features' }
];

const technologies = ['React', 'Node.js', 'SQL Server', 'REST API', 'JavaScript', 'TypeScript'];

const services = ['Application Engineering', 'System Enhancement', 'Feature Development', 'Legacy Integration'];

const testimonial = {
  quote: 'FirstLogic expertly enhanced our application and extended our donor management capabilities. The seamless integration and new features have significantly improved our ability to engage and manage donors effectively.',
  author: 'Architek Solutions USA Team',
  role: 'Architek Solutions USA Management'
};

const ArchitekSolutionsUSAPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-white dark:bg-slate-950">
      <section className="relative isolate overflow-hidden pt-36 pb-32">
        <div className="absolute inset-0">
          <img
            src={heroImage}
            alt="Application Enhancement & Donor Management"
            className="w-full h-full object-cover"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/65 via-slate-950/55 to-slate-950/65" />
          <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-white dark:from-slate-950 to-transparent" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-white drop-shadow-[0_4px_20px_rgba(15,23,42,0.4)]">
          <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-10 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <motion.div
                className="inline-flex items-center px-4 py-2 rounded-full bg-white/10 dark:bg-slate-900/40 backdrop-blur-sm border border-white/40 dark:border-slate-700 mb-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <IconWrapper icon={HiStar} className="w-5 h-5 text-emerald-400 mr-2" />
                <span className="text-sm font-medium text-white">
                  Non-Profit Case Study
                </span>
              </motion.div>

              <motion.h1
                className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4 text-white"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                Application Enhancement & Donor Management
              </motion.h1>
              <motion.p
                className="text-lg text-white/75 mb-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                Architek Solutions USA - USA-Based NGO Organization
              </motion.p>

              <motion.p
                className="text-xl text-white/85 mb-8 leading-relaxed"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
              >
                Application enhancement and development of new donor management features, including system modifications to support extended functionality.
              </motion.p>

              <motion.div
                className="flex flex-wrap items-center gap-6 mb-8"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.8 }}
              >
                <div className="flex items-center">
                  <IconWrapper icon={HiUsers} className="w-5 h-5 text-emerald-400 mr-2" />
                  <span className="text-white/90 font-medium">
                    Architek Solutions USA
                  </span>
                </div>
                <div className="flex items-center">
                  <IconWrapper icon={HiBuildingOffice} className="w-5 h-5 text-emerald-400 mr-2" />
                  <span className="text-white/90 font-medium">
                    Non-Profit
                  </span>
                </div>
                <div className="flex items-center">
                  <IconWrapper icon={HiMapPin} className="w-5 h-5 text-emerald-400 mr-2" />
                  <span className="text-white/90 font-medium">
                    USA
                  </span>
                </div>
              </motion.div>
            </motion.div>

            <motion.div
              className="relative"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <div className="relative w-full h-80 lg:h-[440px] rounded-3xl overflow-hidden shadow-2xl border border-white/30 bg-white/10 backdrop-blur-sm">
                <img
                  src={heroImage}
                  alt="Application Enhancement & Donor Management"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-br from-emerald-600/30 to-slate-900/40" />
                <div className="absolute bottom-4 left-4 right-4 flex flex-wrap gap-3">
                  {services.map(service => (
                    <span
                      key={service}
                      className="px-3 py-1 rounded-full text-xs font-semibold bg-white/90 dark:bg-slate-900/90 text-slate-800 dark:text-white backdrop-blur-sm"
                    >
                      {service}
                    </span>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-16 border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-6">
            {keyMetrics.map((metric, index) => (
              <motion.div
                key={metric.metric}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="p-6 rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-sm"
              >
                <div className="text-sm uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-2">
                  {metric.metric}
                </div>
                <div className="text-3xl font-bold text-slate-900 dark:text-white">
                  {metric.value}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Key Results</h2>
          </motion.div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {results.map((result, index) => (
              <motion.div
                key={result.metric}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="p-6 rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-sm text-center"
              >
                <div className="text-3xl font-bold text-emerald-600 dark:text-emerald-400 mb-2">
                  {result.value}
                </div>
                <div className="text-sm text-slate-600 dark:text-slate-300">
                  {result.metric}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-6">
          <div className="p-8 rounded-3xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-sm">
            <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">Pain Points</h3>
            <ul className="space-y-3">
              {painPoints.map(point => (
                <li key={point} className="flex items-start text-slate-600 dark:text-slate-300">
                  <span className="mt-1 mr-3">
                    <IconWrapper icon={HiMiniSparkles} className="w-5 h-5 text-emerald-500" />
                  </span>
                  {point}
                </li>
              ))}
            </ul>
          </div>
          <div className="p-8 rounded-3xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-sm">
            <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">Strategy</h3>
            <ol className="space-y-4 list-decimal list-inside text-slate-600 dark:text-slate-300">
              {strategy.map(step => (
                <li key={step}>{step}</li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      <section className="py-16 border-b border-slate-200 dark:border-slate-800 bg-slate-50/70 dark:bg-slate-900/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-3xl font-bold text-slate-900 dark:text-white mb-8 text-center">
            Results & Impact
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {outcomes.map(outcome => (
              <motion.div
                key={outcome.title}
                className="p-6 rounded-3xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <div className="text-sm font-semibold text-emerald-600 dark:text-emerald-400 uppercase tracking-wide mb-2">
                  {outcome.title}
                </div>
                <p className="text-slate-600 dark:text-slate-300">{outcome.detail}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-[1.2fr_0.8fr] gap-8">
            <div className="rounded-3xl border border-slate-200 dark:border-slate-800 p-8 bg-white dark:bg-slate-950 shadow-sm">
              <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-6">Engagement Timeline</h3>
              <div className="space-y-6">
                {timeline.map(item => (
                  <div key={item.phase} className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-200 font-semibold flex items-center justify-center">
                      {item.phase.split(' ')[0]}
                    </div>
                    <div>
                      <div className="text-sm uppercase tracking-wide text-slate-500 dark:text-slate-400">
                        {item.duration}
                      </div>
                      <div className="text-lg font-semibold text-slate-900 dark:text-white">
                        {item.phase}
                      </div>
                      <p className="text-slate-600 dark:text-slate-300 text-sm">{item.focus}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-3xl border border-slate-200 dark:border-slate-800 p-8 bg-white dark:bg-slate-950 shadow-sm">
              <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-6">Technologies</h3>
              <div className="flex flex-wrap gap-3 mb-6">
                {technologies.map(tech => (
                  <span
                    key={tech}
                    className="px-3 py-1.5 rounded-full text-sm bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-white"
                  >
                    {tech}
                  </span>
                ))}
              </div>
              
              <div className="space-y-4">
                <div className="p-4 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800">
                  <div className="flex items-center gap-2 mb-2">
                    <IconWrapper icon={HiCog} className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                    <span className="font-semibold text-slate-900 dark:text-white">System Enhancement</span>
                  </div>
                  <p className="text-sm text-slate-600 dark:text-slate-300">
                    Application enhancement with legacy system integration
                  </p>
                </div>

                <div className="p-4 rounded-xl bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800">
                  <div className="flex items-center gap-2 mb-2">
                    <IconWrapper icon={HiUsers} className="w-5 h-5 text-teal-600 dark:text-teal-400" />
                    <span className="font-semibold text-slate-900 dark:text-white">Donor Management</span>
                  </div>
                  <p className="text-sm text-slate-600 dark:text-slate-300">
                    Extended features for comprehensive donor tracking and engagement
                  </p>
                </div>

                <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-900/20 border border-slate-200 dark:border-slate-800">
                  <div className="flex items-center gap-2 mb-2">
                    <IconWrapper icon={HiMapPin} className="w-5 h-5 text-slate-600 dark:text-slate-400" />
                    <span className="font-semibold text-slate-900 dark:text-white">Location</span>
                  </div>
                  <p className="text-sm text-slate-600 dark:text-slate-300">
                    USA-Based NGO Organization
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center p-10 bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-3xl text-slate-900 dark:text-white shadow-sm"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <IconWrapper icon={HiStar} className="w-12 h-12 text-emerald-500 mx-auto mb-6" />
            <blockquote className="text-2xl italic mb-8 leading-relaxed text-slate-700 dark:text-slate-200">
              "{testimonial.quote}"
            </blockquote>
            <div>
              <div className="font-semibold text-lg">{testimonial.author}</div>
              <div className="text-slate-500 dark:text-slate-400">{testimonial.role}</div>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            className="relative p-10 border border-slate-200 dark:border-slate-800 rounded-3xl overflow-hidden bg-white dark:bg-slate-950"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-emerald-50 to-slate-100 dark:from-slate-900 dark:to-slate-950" />
            <div className="relative z-10">
              <motion.h2
                className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-6"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
              >
                Ready to Start Your Engagement?
              </motion.h2>
              <motion.p
                className="text-lg text-slate-600 dark:text-slate-300 mb-8"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                viewport={{ once: true }}
              >
                Let's discuss how we can deliver the same outcomes for your teams.
              </motion.p>
              <motion.div
                className="flex flex-col sm:flex-row gap-4 justify-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                viewport={{ once: true }}
              >
                <motion.button
                  className="px-8 py-4 bg-emerald-600 text-white rounded-full font-semibold text-lg hover:bg-emerald-700 transition-colors"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => (window.location.href = '/contact')}
                >
                  Start Your Project
                </motion.button>
                <motion.button
                  className="px-8 py-4 bg-transparent border-2 border-emerald-600 dark:border-slate-700 text-emerald-600 dark:text-white rounded-full font-semibold text-lg hover:bg-emerald-50 dark:hover:bg-slate-900 transition-all"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => (window.location.href = '/contact')}
                >
                  Schedule Consultation
                </motion.button>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ArchitekSolutionsUSAPage;

